import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { getDeadlineStatus } from '../deadlines';
import {
  getQuantity,
  isBundleOpen,
  istBuendelfaehig,
  getCategory,
  matchFelderFuer,
} from '../bundles';
import { CATEGORY_LABELS } from '../matching';

import css from './AnfrageChips.module.css';

/**
 * FairwayBid – die Eckdaten einer Anfrage, überall gleich
 *
 * Ein Anbieter entscheidet in zwei Sekunden, ob sich das Rechnen lohnt, und
 * er entscheidet es an vier Zahlen: Wie lange noch, wie viel, welcher
 * Rahmen, wohin. Standen die nicht da, musste er jede Anfrage einzeln
 * öffnen – in der Suche, im Dashboard, überall.
 *
 * Deshalb ein Baustein statt drei Kopien. Die Suchergebnisse und die
 * passenden Anfragen im Händlerportal zeigen jetzt dieselben Angaben in
 * derselben Form; was hier geändert wird, ändert sich an beiden Stellen.
 *
 * Als einzelne Chips und nicht als ein mit Punkten verbundener Satz: Der
 * Satz brach auf dem Telefon mitten im Betrag um ("5.000-" / "15.000 EUR").
 * Chips brechen dort um, wo sie dürfen.
 *
 * Was NICHT dabeisteht, ist Absicht: Beschreibung, Leistungen und Bilder
 * gehören auf die Anfrageseite. Eine Übersicht, in der alles steht, ist
 * keine Übersicht mehr.
 */

/**
 * Beschriftung eines Feldes und seiner Werte – aus der Feldkonfiguration.
 *
 * Den Feldschlüssel hinzuschreiben wäre für eine Golfanlage kein Satz,
 * sondern ein Rätsel. Die Beschriftungen kommen deshalb aus derselben
 * Quelle, aus der auch das Anfrageformular seine Auswahl baut.
 */
export const useFeldInfo = () => {
  const config = useConfiguration();
  const felder = config?.listing?.listingFields || [];

  return (key, werte) => {
    const feld = felder.find(f => f?.key === key);
    const label = feld?.showConfig?.label || feld?.saveConfig?.label || key;
    const optionen = feld?.enumOptions || [];
    const lesbar = (Array.isArray(werte) ? werte : [werte])
      .filter(Boolean)
      .map(w => optionen.find(o => o.option === w)?.label || w)
      .join(', ');
    return { label, lesbar };
  };
};

/**
 * @param {Object} listing die Anfrage
 * @param {boolean} zeigeFrist Frist mit anzeigen? In Listen ja; dort, wo die
 *        Frist ohnehin groß danebensteht, wäre sie doppelt.
 * @param {boolean} zeigeKategorie Kategorie mit anzeigen? In den
 *        Suchergebnissen ja – dort steht sonst nur der Titel, und "Ersatz für
 *        Altgerät" sagt nicht, wovon. Im Händlerportal nein: Dort steht sie
 *        schon als Zeile über dem Titel.
 */
const AnfrageChips = ({ listing, className, zeigeFrist = true, zeigeKategorie = false }) => {
  const feldInfo = useFeldInfo();

  const pd = listing?.attributes?.publicData || {};
  const deadline = zeigeFrist ? getDeadlineStatus(listing) : null;

  const menge = getQuantity(listing);
  const budget = feldInfo('budget_range', pd.budget_range).lesbar;
  const land = feldInfo('delivery_country', pd.delivery_country).lesbar;
  const termin = feldInfo('delivery_date', pd.delivery_date).lesbar;
  const buendelbar = isBundleOpen(listing) && istBuendelfaehig(listing);

  // Die Merkmale, die diese Kategorie binden: Antriebsart und Sitzplaetze beim
  // Cart, Ausfuehrung und Farbe beim Range-Ball. Sie stehen direkt hinter der
  // Menge, weil sie zusammen die Position ergeben - "neun Stueck" allein ist
  // keine Kalkulation, "neun Stueck, Elektro, 4 Sitze" ist eine.
  const merkmale = matchFelderFuer(getCategory(listing))
    .map(key => feldInfo(key, pd[key]).lesbar)
    .filter(Boolean);

  // Die Kategorie steht als einzige Angabe an jeder Anfrage - auch an denen,
  // die vor den strukturierten Feldern entstanden sind und ihre Eckdaten noch
  // im Beschreibungstext tragen. Sie ist damit das, was eine Karte in der
  // Suche mindestens sagen kann.
  const kategorie = zeigeKategorie ? CATEGORY_LABELS[getCategory(listing)] || null : null;

  const chips = [
    kategorie,
    menge !== null ? `${menge.toLocaleString('de-DE')} Stück` : null,
    ...merkmale,
    budget || null,
    // Findet die Feldkonfiguration keine Beschriftung, steht dort der rohe
    // Wert - bei Ländern also "de". Zwei Buchstaben groß geschrieben sind
    // wenigstens lesbar.
    land ? (land.length <= 3 ? land.toUpperCase() : land) : null,
    termin || null,
  ].filter(Boolean);

  if (!deadline && chips.length === 0 && !buendelbar) {
    return null;
  }

  return (
    <span className={classNames(css.chips, className)}>
      {/*
        Die Frist steht vorn und trägt als einzige Farbe - sie ist die
        Angabe, nach der ein Anbieter zuerst schaut.
      */}
      {deadline ? (
        <span
          className={classNames(css.chip, css.chipFrist, {
            [css.chipDringend]: deadline.urgent,
          })}
          title={`Angebotsfrist bis ${deadline.dateLabel}`}
        >
          {deadline.kurzLabel || deadline.label}
        </span>
      ) : null}

      {chips.map(text => (
        <span key={text} className={css.chip}>
          {text}
        </span>
      ))}

      {/*
        Keine Eigenschaft der Ware, sondern die Aussicht auf eine größere
        Position - deshalb in der Hausfarbe.
      */}
      {buendelbar ? (
        <span
          className={classNames(css.chip, css.chipBuendel)}
          title="Diese Anlage ist für eine gemeinsame Bestellung offen"
        >
          bündelbar
        </span>
      ) : null}
    </span>
  );
};

export default AnfrageChips;
